/** @jsx jsx */
import { type SerializedStyles, css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef } from 'react';

import { breakpoints } from '@coursera/cds-core';

import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';

type Props = {
  topOffset?: number;
  additionalStyles?: SerializedStyles;
};

const chatTopOffset = (top: number) => css`
  top: ${top}px;
  height: calc(100% - ${top}px);
`;

const styles = {
  chatStyles: css`
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 999;
    box-shadow: 0 0 28px -6px rgb(0 0 0 / 16%), 0 0 28px -12px rgb(0 0 0 / 16%);
    transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
    transform-origin: bottom left;

    ${breakpoints.down('sm')} {
      box-shadow: none;
    }
  `,
  fullScreenChatSize: css`
    width: 100%;
    height: 100%;
  `,
  childContainer: css`
    position: relative;
    height: 100%;

    ${breakpoints.down('sm')} {
      max-width: 100%;
      margin: 0;
      height: 100%;
    }
  `,
  hidden: css`
    display: none;
  `,
};

const FullScreenCoachChatContainer = ({ children, topOffset, additionalStyles }: React.PropsWithChildren<Props>) => {
  const { coachOpen, triggerElementRef, setTriggerElementRef } = useCoachState();
  const bodyOverflowRef = useRef<string | null>(null);

  useEffect(() => {
    // Don't modify page scroll while the chat is not yet open.
    if (!coachOpen) return undefined;

    bodyOverflowRef.current = document.body.style.overflow || '';
    // disable page scrolling when the chat is fullscreen
    document.body.style.overflow = 'hidden';

    return () => {
      // always restore to visible on unmount
      document.body.style.overflow = bodyOverflowRef.current || 'auto';
    };
  }, [coachOpen]);

  useEffect(() => {
    if (!coachOpen && triggerElementRef.current) {
      // clear the ref after transition end and React lifecycle methods completed
      const tid = setTimeout(() => {
        setTriggerElementRef(null);
      }, 300);
      return () => clearTimeout(tid);
    }
    return undefined;
  }, [coachOpen, triggerElementRef, setTriggerElementRef]);

  return (
    <React.Fragment>
      {/* Keep the chat container mounted even when closed so CoachChat's session-init
          effect runs on page load and history is pre-fetched before the user first opens. */}
      <div
        css={[
          styles.chatStyles,
          styles.fullScreenChatSize,
          !coachOpen && styles.hidden,
          topOffset ? chatTopOffset(topOffset) : undefined,
          additionalStyles,
        ]}
        aria-hidden={!coachOpen || undefined}
        aria-modal={coachOpen || undefined}
        role="dialog"
      >
        <div css={[styles.childContainer, !coachOpen && styles.hidden]}>{children}</div>
      </div>
    </React.Fragment>
  );
};

export default FullScreenCoachChatContainer;
